import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";

type ReleaseCacheEntry = { ts: number; release: any };
const RELEASE_CACHE_MS = 60_000;
const releaseCache = new Map<string, ReleaseCacheEntry>();
const inflightRelease = new Map<string, Promise<any>>();
const releaseStorageKey = (releaseId: string) => `nr_release_cache:${releaseId}`;

export const useRelease = (id?: string) => {
  const [release, setRelease] = useState<any>();
  const [fetching, setFetching] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const lastJsonUpdateSigRef = useRef<string>("");

  const params = useParams();
  const releaseId = id ?? params.id;
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
    return { Accept: "application/json", Authorization: `Bearer ${token}` };
  }, []);

  const extractBackendMessage = useCallback((error: any): string => {
    const data = error?.response?.data;
    if (!data) return error?.message || "Something went wrong.";
    let backendMessage =
      data?.error ||
      data?.message ||
      "Something went wrong.";

    if (data?.errors && typeof data.errors === "object") {
      const firstKey = Object.keys(data.errors)[0];
      const firstError = data.errors[firstKey]?.[0]?.message;
      if (firstError) backendMessage = firstError;
    }
    return backendMessage;
  }, []);
  const [relId, setRelId] = useState<string | null>(params.id || localStorage.getItem('releaseId'));
  

  useEffect(() => {
    if (relId) {
      localStorage.setItem('releaseId', relId);
    }
  }, [relId]);

  const hydrateReleaseCache = useCallback((rid: string) => {
    if (!rid) return;
    if (releaseCache.has(rid)) return;
    try {
      const raw = sessionStorage.getItem(releaseStorageKey(rid));
      if (!raw) return;
      const parsed = JSON.parse(raw) as ReleaseCacheEntry;
      if (parsed?.ts && parsed?.release) {
        releaseCache.set(rid, parsed);
      }
    } catch {
      // ignore
    }
  }, []);

  const fetchRelease = useCallback(
    async (opts?: { backgroundRefresh?: boolean }) => {
    if (!releaseId) {
      setFetching(false);
        setError(null);
      return;
    }

      const rid = String(releaseId);
      hydrateReleaseCache(rid);

      const cached = releaseCache.get(rid);
      const now = Date.now();
      const isCacheFresh = cached && now - cached.ts < RELEASE_CACHE_MS;

      // If asked, show cached immediately (no blocking spinner), then refresh in background.
      if (opts?.backgroundRefresh && isCacheFresh) {
        setRelease(cached!.release);
      setFetching(false);
    }

      // Deduplicate concurrent calls (NewRelease + sidebar + step components)
      if (inflightRelease.has(rid)) {
        try {
          const existing = await inflightRelease.get(rid)!;
          setRelease(existing);
          setError(null);
          return existing;
        } catch (e) {
          setError(extractBackendMessage(e));
          throw e;
        }
      }

      const shouldBlock = !opts?.backgroundRefresh || !isCacheFresh;
      if (shouldBlock) setFetching(true);
      setError(null);

      const p = axios
        .get(`${API_BASE_URL}${API_URLS.Release.replace("{release}", rid)}`, { headers: getAuthHeaders() })
        .then((response) => {
          const data = response.data?.data;
          setRelease(data);
          const entry = { ts: Date.now(), release: data };
          releaseCache.set(rid, entry);
          try {
            sessionStorage.setItem(releaseStorageKey(rid), JSON.stringify(entry));
          } catch {
            // ignore
          }
          return data;
        })
        .catch((e) => {
          setError(extractBackendMessage(e));
          throw e;
        })
        .finally(() => {
          inflightRelease.delete(rid);
          if (shouldBlock) setFetching(false);
        });

      inflightRelease.set(rid, p);
      return await p;
    },
    [extractBackendMessage, getAuthHeaders, hydrateReleaseCache, releaseId]
  );

  const upDateRole = async (releaseId: number, artistId: number, role: string) => {
    if (!releaseId || !artistId || !role) return;

    try {
      setUpdating(true);
      const response = await axios.post(
        `${API_BASE_URL}${API_URLS.Role
          .replace("{release}", String(releaseId))
          .replace("{artist}", String(artistId))}`,
        { role },
        { headers: getAuthHeaders() }
      );

      await fetchRelease();
    } catch (error) {
      console.error("Update role error:", error);
    } finally {
      setUpdating(false);
    }
  };

  const updateRelease = useCallback(async (data: any) => {
    if (!releaseId) return;

    try {
      // Some call sites historically did `updateRelease(id)` just to "save".
      // This is NOT a valid payload and causes backend errors. Treat it as a safe no-op.
      if (data == null) return;
      if (typeof data === "string" || typeof data === "number") {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[useRelease.updateRelease] Ignoring invalid call. Pass a payload object/FormData instead.", {
            releaseId,
            argType: typeof data,
            argValue: data,
          });
        }
        return;
      }

      // If it's a JSON-like object, skip POST when nothing changed (prevents spam on tab/step swaps).
      // FormData is always sent (can't reliably diff it, and it's used for cover upload).
      const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
      if (!isFormData && typeof data === "object") {
        try {
          const jsonSig = JSON.stringify(data, Object.keys(data).sort());
          if (jsonSig === lastJsonUpdateSigRef.current) {
            if (process.env.NODE_ENV !== "production") {
              console.log("[useRelease.updateRelease] Skipping identical payload", { releaseId });
            }
            return;
          }
          lastJsonUpdateSigRef.current = jsonSig;
        } catch {
          // If stringify fails, proceed with the request.
        }
      }

      setFetching(true);
      const response = await axios.post(
        `${API_BASE_URL}${API_URLS.Release.replace("{release}", String(releaseId))}`,
        data,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM)}`
          }
        }
      );

      // Invalidate cache immediately; then refresh.
      try {
        const rid = String(releaseId);
        releaseCache.delete(rid);
        sessionStorage.removeItem(releaseStorageKey(rid));
      } catch {
        // ignore
      }
      await fetchRelease({ backgroundRefresh: true });
      return response.data;
    } catch (error: any) {
      console.error("Update error:", error);

      if (error.response?.status === 404) {
        navigate("/404");
        return;
      }
      const data = error.response?.data;

      let backendMessage =
        data?.error ||
        data?.message ||
        "Something went wrong.";

      if (data?.errors && typeof data.errors === "object") {
        const firstKey = Object.keys(data.errors)[0];
        const firstError = data.errors[firstKey]?.[0]?.message;
        if (firstError) {
          backendMessage = firstError;
        }
      }

      throw new Error(backendMessage);
    } finally {
      setFetching(false);
    }
  }, [fetchRelease, releaseId]);

  const reorderReleaseArtists = async (releaseId: number, data: any) => {
    if (!releaseId) return;
    try {
      setFetching(true);
      const response = await axios.patch(
        `${API_BASE_URL}${API_URLS.ReleaseArtistsReorder.replace("{releaseId}", String(releaseId))}`,
        data,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM)}`
          }
        }
      );
      await fetchRelease();
      return response.data;
    } catch (e) {
      console.error("Error reordering artists:", e);
      throw e;
    } finally {
      setFetching(false);
    }
  };

  const submitRelease = async (releaseId: number, status = "queued") => {
    if (!releaseId) return;
    try {
      setUpdating(true);
      const response = await axios.post(
        `${API_BASE_URL}${API_URLS.ReleasesSubmit.replace("{releaseId}", String(releaseId))}`,
        { status },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM)}`,
          },
        }
      );
      return response.data;
    } catch (e) {
      console.error("Error submitting release:", e);
      throw e;
    } finally {
      setUpdating(false);
    }
  };

  const validateRelease = useCallback(
    async (releaseId: number) => {
      if (!releaseId) return null;
      try {
        const response = await axios.get(
          `${API_BASE_URL}${API_URLS.ReleaseValidate.replace("{release}", String(releaseId))}`,
          { headers: getAuthHeaders() }
        );
        if (process.env.NODE_ENV !== "production") {
          // Log full backend response for debugging validation issues
          // eslint-disable-next-line no-console
          console.log("[Release validation]", response.data);
        }
        return response.data;
      } catch (e) {
        console.error("Error validating release:", e);
        throw new Error(extractBackendMessage(e));
      }
    },
    [getAuthHeaders, extractBackendMessage]
  );

  const getCheckout = async (
    releaseId: number,
    releaseTypeId: number,
    deliveryOptionId: number | null,
    opts?: { silent?: boolean }
  ) => {
    if (!releaseId) return;
    try {
      if (!opts?.silent) setUpdating(true);
      const queryParams = new URLSearchParams({
        release_type_id: String(releaseTypeId),
        ...(deliveryOptionId != null ? { delivery_option_id: String(deliveryOptionId) } : {}),
      } as any);
      const response = await axios.get(
        `${API_BASE_URL}${API_URLS.releaseCheckout.replace("{release}", String(releaseId))}?${queryParams.toString()}`,
        { headers: { Accept: "application/json", Authorization: `Bearer ${localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM)}` } }
      );
      return response.data;
    } catch (e) {
      console.error("Error fetching checkout data:", e);
      throw new Error(extractBackendMessage(e));
    } finally {
      if (!opts?.silent) setUpdating(false);
    }
  };

  const releasePay = async (releaseId: number, deliveryOptionId = 1, releaseTypeId?: number) => {
    if (!releaseId) return;

    try {
      setUpdating(true);

      const response = await axios.post(
        `${API_BASE_URL}${API_URLS.releasePay.replace("{release}", String(releaseId))}`,
        {
          ...(releaseTypeId != null ? { release_type_id: releaseTypeId } : {}),
          delivery_option_id: deliveryOptionId,
          success_url: `${location.origin}/releases/new-release/${relId}?step=paidPlatform`,
          cancel_url: `${location.origin}/releases/new-release/${relId}?step=platform`
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM)}`
          }
        }
      );


      const checkoutUrl =
        response.data?.data?.checkout_url ??
        response.data?.checkout_url ??
        response.data?.data?.url ??
        response.data?.url;
      if (checkoutUrl) window.location.href = checkoutUrl;

      return response.data;
    } catch (e) {
      console.error("Error creating checkout session:", e);
      throw new Error(extractBackendMessage(e));
    } finally {
      setUpdating(false);
    }
  };

  return { release, fetching, updating, error, updateRelease, upDateRole, fetchRelease, reorderReleaseArtists, submitRelease, getCheckout, releasePay, validateRelease };
};
